import { HorizontalTidyUp } from '@icon-park/react';
import { memo, useCallback, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import { globalMessages } from '@didaskill/ui/intl';
import { useBoolState } from '@didaskill/ui/utils';

import { Checkbox } from '../../forms/checkbox/Checkbox';
import { Button } from '../../general/button/Button';
import { Popover } from '../../general/popover/Popover';
import { Tooltip } from '../../general/tooltip/Tooltip';
import { Typography } from '../../general/typography/Typography';
import messages from './TableColumnPicker.messages';
import * as styles from './TableColumnPicker.styles';

export type TableColumnOption<TKey extends string = string> = {
  key: TKey;
  label: string;
};

export type TableColumnPickerProps<TKey extends string = string> = {
  /** Columns the user can show or hide, in table display order. */
  columns: TableColumnOption<TKey>[];
  visibleColumnKeys: TKey[];
  onChangeVisibleColumnKeys: (columnKeys: TKey[]) => void;
};

/**
 * Keeps the selected columns in table display order.
 * @param columns Columns of the picker, in table display order.
 * @param columnKeys Selected column keys, in any order.
 * @returns Selected keys ordered like the table.
 */
function sortColumnKeys<TKey extends string>(
  columns: TableColumnOption<TKey>[],
  columnKeys: TKey[],
): TKey[] {
  const selectedKeys = new Set(columnKeys);

  return columns.map(({ key }) => key).filter((columnKey) => selectedKeys.has(columnKey));
}

/**
 * Popover to choose which table columns are displayed. At least one column stays selected.
 * @param props Component props.
 * @returns Column picker trigger and popover.
 */
export const TableColumnPickerBase = <TKey extends string = string>({
  columns,
  visibleColumnKeys,
  onChangeVisibleColumnKeys,
}: TableColumnPickerProps<TKey>): JSX.Element => {
  const intl = useIntl();
  const [isOpen, , onClose, onToggle] = useBoolState(false);
  const [draftColumnKeys, setDraftColumnKeys] = useState(visibleColumnKeys);

  const onToggleColumn = useCallback(
    (columnKey: TKey, checked: boolean): void => {
      setDraftColumnKeys((previousColumnKeys) => {
        if (checked) {
          return sortColumnKeys(columns, [...previousColumnKeys, columnKey]);
        }

        if (previousColumnKeys.length <= 1) {
          return previousColumnKeys;
        }

        return previousColumnKeys.filter((currentColumnKey) => currentColumnKey !== columnKey);
      });
    },
    [columns],
  );

  const onConfirm = useCallback((): void => {
    onChangeVisibleColumnKeys(sortColumnKeys(columns, draftColumnKeys));
    onClose();
  }, [columns, draftColumnKeys, onChangeVisibleColumnKeys, onClose]);

  const onTogglePicker = useCallback((): void => {
    if (!isOpen) {
      setDraftColumnKeys(visibleColumnKeys);
    }

    onToggle();
  }, [isOpen, onToggle, visibleColumnKeys]);

  return (
    <Popover
      interactive
      placement="bottom-end"
      visible={isOpen}
      onClickOutside={onClose}
      content={
        <div css={styles.content}>
          <div css={styles.header}>
            <Typography variant={Typography.VARIANTS.BODY_MEDIUM_BOLD}>
              <FormattedMessage {...messages.TITLE} />
            </Typography>
            <Typography variant={Typography.VARIANTS.BODY_SMALL_REGULAR} css={styles.subtitle}>
              <FormattedMessage
                {...messages.SELECTION_COUNT}
                values={{
                  selected: draftColumnKeys.length,
                  total: columns.length,
                }}
              />
            </Typography>
          </div>

          <div
            css={styles.columnsList}
            role="group"
            aria-label={intl.formatMessage(messages.TITLE)}
          >
            {columns.map((column) => (
              <Checkbox
                key={column.key}
                checked={draftColumnKeys.includes(column.key)}
                disabled={draftColumnKeys.includes(column.key) && draftColumnKeys.length <= 1}
                onChange={(checked) => onToggleColumn(column.key, checked)}
                description={column.label}
              />
            ))}
          </div>

          <div css={styles.actions}>
            <Button outline variant={Button.VARIANTS.NEUTRAL} onClick={onClose}>
              <FormattedMessage {...globalMessages.CANCEL} />
            </Button>
            <Button variant={Button.VARIANTS.PRIMARY} onClick={onConfirm}>
              <FormattedMessage {...globalMessages.OK} />
            </Button>
          </div>
        </div>
      }
    >
      <div>
        <Tooltip content={<FormattedMessage {...messages.BUTTON} />}>
          <div>
            <Button
              outline
              variant={Button.VARIANTS.NEUTRAL}
              icon={<HorizontalTidyUp />}
              aria-label={intl.formatMessage(messages.BUTTON)}
              onClick={onTogglePicker}
              isActive={isOpen}
            />
          </div>
        </Tooltip>
      </div>
    </Popover>
  );
};

TableColumnPickerBase.displayName = 'TableColumnPicker';

export const TableColumnPicker = memo(TableColumnPickerBase) as typeof TableColumnPickerBase;
